import { Component, EventEmitter, Output, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormArray } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HeaderToolbarComponent } from '../../shared/header-toolbar/header-toolbar.component';
import { AuthService } from '../../services/auth.service';
import { QuoteService } from '../../services/quote.service';
import { ProgramService } from '../../services/program.service';
import { formatUsd } from '../../shared/formatting/usd-currency';
import { InsuranceCompanyService } from '../../services/insurance-company.service';
import {
  CreateQuoteRequest,
  UpdateQuoteRequest,
  Quote,
  PolicyDto,
  LoanType,
  Coverage,
  CancelDays,
  QuoteStatus,
  FinanceProgram,
  InsuranceCompany
} from '../../models/quote.model';

@Component({
  selector: 'app-quote',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTabsModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckboxModule,
    HeaderToolbarComponent
  ],
  templateUrl: './quote.component.html',
  styleUrls: ['./quote.component.scss']
})
export class QuoteComponent implements OnInit {
  @Output() quoteSaved = new EventEmitter<Quote>();

  quoteForm: FormGroup;
  isSaving = false;
  isLoading = false;
  isNewQuote: boolean = true;
  quoteId: number | null = null;
  quote: Quote | null = null;

  // Dropdown options
  programs: FinanceProgram[] = [];
  insuranceCompanies: InsuranceCompany[] = [];

  // Enum options

  loanTypeOptions = [
    { value: LoanType.Commercial, label: 'Commercial' },
    { value: LoanType.Personal, label: 'Personal' }
  ];

  coverageOptions = [
    { value: Coverage.Aircraft, label: 'Aircraft' }
  ];

  cancelDaysOptions = [
    { value: CancelDays.D10, label: '10 Days' },
    { value: CancelDays.D15, label: '15 Days' },
    { value: CancelDays.D20, label: '20 Days' },
    { value: CancelDays.D25, label: '25 Days' },
    { value: CancelDays.D30, label: '30 Days' },
    { value: CancelDays.D35, label: '35 Days' },
    { value: CancelDays.D45, label: '45 Days' },
    { value: CancelDays.D60, label: '60 Days' }
  ];

  stateOptions = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
    { value: 'DC', label: 'District of Columbia' }
  ];

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private quoteService: QuoteService,
    private programService: ProgramService,
    private insuranceCompanyService: InsuranceCompanyService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar
  ) {
    this.quoteForm = this.fb.group({
      // Customer Information
      agentName: ['BWI Aviation Insurance Agency, Inc'],
      insuredName: ['', [Validators.required, Validators.minLength(2)]],
      addressLine1: [''],
      addressLine2: [''],
      city: [''],
      state: [''],
      postalCode: [''],
      phone: ['', [Validators.pattern(/^[0-9]{3}-[0-9]{3}-[0-9]{4}$/)]],
      email: ['', [Validators.email]],
      customerNumber: [''],

      // Quote Configuration
      loanType: [LoanType.Commercial, [Validators.required]],
      programId: [null, [Validators.required]],
      insuranceCompanyId: [null],

      // Policies
      policies: this.fb.array([])
    });
  }

  ngOnInit(): void {
    this.loadDropdownData();

    this.route.params.subscribe(params => {
      this.quoteId = params['id'] ? +params['id'] : null;
      this.isNewQuote = !this.quoteId;

      if (this.quoteId) {
        this.loadQuote(this.quoteId);
      } else {
        this.updateFormMode();
        this.addPolicy(); // Add one policy by default for new quotes
      }
    });
  }

  private loadDropdownData(): void {
    // Load programs
    this.programService.getPrograms().subscribe({
      next: (programs) => {
        this.programs = programs;
      },
      error: (error) => {
        console.error('Error loading programs:', error);
        this.snackBar.open('Failed to load programs', 'Close', {
          duration: 3000,
          verticalPosition: 'bottom',
          panelClass: ['error']
        });
      }
    });

    // Load insurance companies
    this.insuranceCompanyService.getInsuranceCompanies().subscribe({
      next: (companies) => {
        this.insuranceCompanies = companies;
      },
      error: (error) => {
        console.error('Error loading insurance companies:', error);
        this.snackBar.open('Failed to load insurance companies', 'Close', {
          duration: 3000,
          verticalPosition: 'bottom',
          panelClass: ['error']
        });
      }
    });
  }

  private loadQuote(id: number): void {
    this.isLoading = true;
    this.quoteService.getQuote(id).subscribe({
      next: (quote) => {
        this.quote = quote;
        this.updateFormMode();
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error loading quote:', error);
        this.snackBar.open('Failed to load quote. Please try again.', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['error']
        });
        this.router.navigate(['/quotes']);
      }
    });
  }

  private updateFormMode(): void {
    if (this.quote && !this.isNewQuote) {
      // Populate form with existing quote data
      this.quoteForm.patchValue({
        agentName: this.quote.agentName,
        insuredName: this.quote.insuredName,
        addressLine1: this.quote.addressLine1,
        addressLine2: this.quote.addressLine2,
        city: this.quote.city,
        state: this.quote.state,
        postalCode: this.quote.postalCode,
        phone: this.quote.phone,
        email: this.quote.email,
        customerNumber: this.quote.customerNumber,
        loanType: this.quote.loanType,
        programId: this.quote.programId,
        insuranceCompanyId: this.quote.insuranceCompanyId
      });

      // Load policies
      this.policiesFormArray.clear();
      this.quote.policies.forEach(policy => {
        this.addPolicy(policy);
      });
    } else {
      // Clear form for new quote
      this.quoteForm.patchValue({
        agentName: 'BWI Aviation Insurance Agency, Inc',
        loanType: LoanType.Commercial
      });
    }
  }

  // Policy management methods
  get policiesFormArray(): FormArray {
    return this.quoteForm.get('policies') as FormArray;
  }

  addPolicy(policy?: any): void {
    const effectiveDate = policy?.effectiveDate || new Date();
    const expirationDate = policy?.expirationDate || this.calculateExpirationDate(effectiveDate);
    const premiumAmount = policy?.premiumAmount || 0;
    const downAmount = policy?.downAmount || this.calculateDownAmount(premiumAmount);

    const policyForm = this.fb.group({
      policyNumber: [policy?.policyNumber || '', [Validators.required]],
      coverage: [policy?.coverage || Coverage.Aircraft, [Validators.required]],
      effectiveDate: [effectiveDate, [Validators.required]],
      expirationDate: [expirationDate],
      premiumAmount: [premiumAmount, [Validators.required, Validators.min(0)]],
      taxesAmount: [policy?.taxesAmount || 0, [Validators.required, Validators.min(0)]],
      feesAmount: [policy?.feesAmount || 0, [Validators.required, Validators.min(0)]],
      minEarnedPercent: [policy?.minEarnedPercent || 0, [Validators.min(0), Validators.max(100)]],
      minEarnedAmount: [policy?.minEarnedAmount || 0, [Validators.min(0)]],
      downAmount: [downAmount, [Validators.min(0)]],
      cancelDays: [policy?.cancelDays || CancelDays.D10, [Validators.required]],
      auditable: [policy?.auditable || false],
      lossPayeeRequired: [policy?.lossPayeeRequired || false],
      lossPayeeRequest: [policy?.lossPayeeRequest || false]
    });

    // Add change listeners for effective date and premium amount
    this.addPolicyChangeListeners(policyForm);

    this.policiesFormArray.push(policyForm);
  }

  removePolicy(index: number): void {
    this.policiesFormArray.removeAt(index);
  }

  private calculateExpirationDate(effectiveDate: Date): Date {
    const expirationDate = new Date(effectiveDate);
    expirationDate.setFullYear(expirationDate.getFullYear() + 1);
    return expirationDate;
  }

  private calculateDownAmount(premiumAmount: number): number {
    return Math.round(premiumAmount * 0.1 * 100) / 100; // Round to 2 decimal places
  }

  private addPolicyChangeListeners(policyForm: FormGroup): void {
    // Listen for effective date changes to update expiration date
    policyForm.get('effectiveDate')?.valueChanges.subscribe((effectiveDate: Date) => {
      if (effectiveDate) {
        const expirationDate = this.calculateExpirationDate(effectiveDate);
        policyForm.get('expirationDate')?.setValue(expirationDate, { emitEvent: false });
      }
    });

    // Listen for premium amount changes to update down amount
    policyForm.get('premiumAmount')?.valueChanges.subscribe((premiumAmount: number) => {
      if (premiumAmount && premiumAmount > 0) {
        const downAmount = this.calculateDownAmount(premiumAmount);
        policyForm.get('downAmount')?.setValue(downAmount, { emitEvent: false });
      }
    });
  }

  private havePoliciesBeenModified(): boolean {
    if (!this.quote || this.isNewQuote) {
      return false;
    }

    const currentPolicies = this.policiesFormArray.value;
    const originalPolicies = this.quote.policies;

    // Check if number of policies changed
    if (currentPolicies.length !== originalPolicies.length) {
      return true;
    }

    // Check if any policy content changed
    for (let i = 0; i < currentPolicies.length; i++) {
      const current = currentPolicies[i];
      const original = originalPolicies[i];

      if (
        current.policyNumber !== original.policyNumber ||
        current.coverage !== original.coverage ||
        new Date(current.effectiveDate).getTime() !== new Date(original.effectiveDate).getTime() ||
        (current.expirationDate && original.expirationDate &&
         new Date(current.expirationDate).getTime() !== new Date(original.expirationDate).getTime()) ||
        current.premiumAmount !== original.premiumAmount ||
        current.taxesAmount !== original.taxesAmount ||
        current.feesAmount !== original.feesAmount ||
        current.minEarnedPercent !== original.minEarnedPercent ||
        current.minEarnedAmount !== original.minEarnedAmount ||
        current.downAmount !== original.downAmount ||
        current.cancelDays !== original.cancelDays ||
        current.auditable !== original.auditable ||
        current.lossPayeeRequired !== original.lossPayeeRequired ||
        current.lossPayeeRequest !== original.lossPayeeRequest
      ) {
        return true;
      }
    }

    return false;
  }

  onSaveQuote(): void {
    if (this.quoteForm.valid && this.policiesFormArray.length > 0) {
      this.isSaving = true;
      const formData = this.quoteForm.value;

      if (this.isNewQuote) {
        // Create new quote
        const createRequest: CreateQuoteRequest = {
          agentName: formData.agentName,
          insuredName: formData.insuredName,
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: formData.city,
          state: formData.state,
          postalCode: formData.postalCode,
          phone: formData.phone,
          email: formData.email,
          customerNumber: formData.customerNumber,
          loanType: formData.loanType,
          programId: formData.programId,
          insuranceCompanyId: formData.insuranceCompanyId,
          policies: formData.policies.map((policy: any) => ({
            policyNumber: policy.policyNumber,
            coverage: policy.coverage,
            effectiveDate: policy.effectiveDate,
            expirationDate: policy.expirationDate,
            premiumAmount: policy.premiumAmount,
            taxesAmount: policy.taxesAmount,
            feesAmount: policy.feesAmount,
            minEarnedPercent: policy.minEarnedPercent,
            minEarnedAmount: policy.minEarnedAmount,
            downAmount: policy.downAmount,
            cancelDays: policy.cancelDays,
            auditable: policy.auditable,
            lossPayeeRequired: policy.lossPayeeRequired,
            lossPayeeRequest: policy.lossPayeeRequest
          }))
        };
        this.createQuote(createRequest);
      } else {
        // Update existing quote
        const updateRequest: UpdateQuoteRequest = {
          agentName: formData.agentName,
          insuredName: formData.insuredName,
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: formData.city,
          state: formData.state,
          postalCode: formData.postalCode,
          phone: formData.phone,
          email: formData.email,
          customerNumber: formData.customerNumber,
          loanType: formData.loanType,
          programId: formData.programId,
          insuranceCompanyId: formData.insuranceCompanyId,
          quoteStatus: this.quote?.status || QuoteStatus.Quoted
        };

        // Check if policies have been modified
        const policiesModified = this.havePoliciesBeenModified();
        if (policiesModified) {
          this.snackBar.open(
            'Policy updates are not currently supported. Only quote information will be updated. To modify policies, please create a new quote.',
            'Close',
            {
              duration: 8000,
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
              panelClass: ['warning']
            }
          );
        }

        this.updateQuote(updateRequest);
      }
    } else {
      this.markFormGroupTouched();
      if (this.policiesFormArray.length === 0) {
        this.snackBar.open('Please add at least one policy', 'Close', {
          duration: 3000,
          verticalPosition: 'bottom',
          panelClass: ['warning']
        });
      }
    }
  }

  private createQuote(quoteData: CreateQuoteRequest): void {
    this.quoteService.createQuote(quoteData).subscribe({
      next: (newQuote) => {
        this.isSaving = false;
        this.snackBar.open('Quote created successfully!', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['success']
        });

        this.router.navigate(['/quotes']);
      },
      error: (error) => {
        this.isSaving = false;
        console.error('Error creating quote:', error);
        this.snackBar.open('Failed to create quote. Please try again.', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['error']
        });
      }
    });
  }

  private updateQuote(quoteData: UpdateQuoteRequest): void {
    if (!this.quoteId) return;

    this.quoteService.updateQuote(this.quoteId, quoteData).subscribe({
      next: (updatedQuote) => {
        this.isSaving = false;
        this.snackBar.open('Quote updated successfully!', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['success']
        });

        // Update the local quote object with the updated data
        this.quote = updatedQuote;
        this.updateFormMode();
      },
      error: (error) => {
        this.isSaving = false;
        console.error('Error updating quote:', error);
        this.snackBar.open('Failed to update quote. Please try again.', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['error']
        });
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/quotes']);
  }

  onResetForm(): void {
    this.updateFormMode();
  }

  getSubtitle(): string {
    return this.isNewQuote
      ? 'Add a new customer quote to the system'
      : 'Update the customer quote information';
  }

  getSubmitButtonText(): string {
    return this.isNewQuote ? 'Create Quote' : 'Update Quote';
  }

  getTitle(): string {
    return this.isNewQuote ? 'Create New Quote' : 'Edit Quote';
  }

  private markFormGroupTouched(): void {
    Object.keys(this.quoteForm.controls).forEach(key => {
      const control = this.quoteForm.get(key);
      control?.markAsTouched();
    });
  }

  /**
   * C8 · format the phone as XXX-XXX-XXXX while typing WITHOUT blocking backspace/delete.
   *
   * The old version appended a trailing dash at exactly 3 and 6 digits (the `>= 3`/`>= 6`
   * branches), so deleting the area code was impossible: removing the dash instantly re-inserted
   * it, trapping the caret at "703-". Two fixes:
   *   1. Only insert a separator when digits follow it (`>` instead of `>=`) — never a trailing dash.
   *   2. On a delete/backspace, don't reformat at all — respect exactly what the user removed
   *      (re-inserting separators fights the delete). Formatting is finalized on blur.
   */
  formatPhoneNumber(event: any): void {
    const input = event.target as HTMLInputElement;
    const isDelete = typeof event.inputType === 'string' && event.inputType.startsWith('delete');

    if (isDelete) {
      // Respect the deletion — sync the raw text to the control, no re-formatting/caret fights.
      this.quoteForm.get('phone')?.setValue(input.value);
      return;
    }

    const value = this.formatPhoneDigits(input.value);
    input.value = value;
    this.quoteForm.get('phone')?.setValue(value);
  }

  /** C8 · normalize to XXX-XXX-XXXX once the user leaves the field (tidies up mid-edit deletes). */
  formatPhoneOnBlur(event: any): void {
    const input = event.target as HTMLInputElement;
    const value = this.formatPhoneDigits(input.value);
    input.value = value;
    this.quoteForm.get('phone')?.setValue(value);
  }

  /** Strip to at most 10 digits and hyphenate, inserting a dash only when digits follow it. */
  private formatPhoneDigits(raw: string): string {
    const digits = (raw || '').replace(/\D/g, '').substring(0, 10);
    if (digits.length > 6) {
      return `${digits.substring(0, 3)}-${digits.substring(3, 6)}-${digits.substring(6)}`;
    }
    if (digits.length > 3) {
      return `${digits.substring(0, 3)}-${digits.substring(3)}`;
    }
    return digits;
  }

  getUserDisplayName(): string {
    return this.authService.getUserDisplayName();
  }

  onLogout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  onBack(): void {
    this.router.navigate(['/quotes']);
  }

  // ---------------------------------------------------------------------------
  //  Presentation-only helpers (black + gold restyle).
  //  Pure/derived: they read existing form + quote state and never mutate it,
  //  so no reactive-form logic or data flow is affected.
  // ---------------------------------------------------------------------------
  readonly QuoteStatus = QuoteStatus;

  private sumPolicyField(field: string): number {
    return this.policiesFormArray.controls.reduce((total, ctrl) => {
      return total + (Number(ctrl.get(field)?.value) || 0);
    }, 0);
  }

  getTotalPremium(): number {
    return this.sumPolicyField('premiumAmount');
  }

  getTotalCharges(): number {
    return this.sumPolicyField('premiumAmount')
      + this.sumPolicyField('taxesAmount')
      + this.sumPolicyField('feesAmount');
  }

  getTotalDown(): number {
    return this.sumPolicyField('downAmount');
  }

  getAmountFinanced(): number {
    return Math.max(0, this.getTotalPremium() - this.getTotalDown());
  }

  formatWhole(value: number | null | undefined): string {
    return formatUsd(Number(value) || 0);
  }

  getStatusMeta(): { cls: string; label: string } {
    switch (this.quote?.status) {
      case QuoteStatus.Quoted:     return { cls: 'badge--quoted',     label: 'Quoted' };
      case QuoteStatus.InProgress: return { cls: 'badge--inprogress', label: 'In progress' };
      case QuoteStatus.Complete:   return { cls: 'badge--complete',   label: 'Complete' };
      case QuoteStatus.Grace:      return { cls: 'badge--grace',      label: 'Grace period' };
      case QuoteStatus.Delinquent: return { cls: 'badge--delinquent', label: 'Delinquent' };
      case QuoteStatus.Cancelled:  return { cls: 'badge--cancelled',  label: 'Cancelled' };
      default:                     return { cls: 'badge--quoted',     label: 'Quoted' };
    }
  }
}
